import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Send, X, Sparkles, Trash2, ArrowUpRight, Loader2,
} from 'lucide-react';
import client from '../../lib/api';

/**
 * Panelin yardım penceresi. Kapalıyken sağ kenarın ortasına yapışan dikey bir
 * sekme olarak durur (canlı destek widget'larındaki yer): sağ alt köşede
 * duran düğme, ekranların alt eylem çubuklarını örtüyordu.
 *
 * Asistan yalnızca **bu panel** hakkında konuşur; kapsam sunucudaki sistem
 * talimatıyla çizilir (bkz. backend/services/admin_assistant.py), istemci
 * yalnızca soruyu taşır. Sohbet geçmişi bilinçli olarak bileşen state'inde
 * durur ve hiçbir yere kaydedilmez: yöneticinin serbest metni kişisel veri
 * taşıyabilir.
 *
 * Yanıt **düz metin olarak** çizilir (`dangerouslySetInnerHTML` yok): metin
 * üçüncü taraf bir modelden gelir ve panel oturumunun içinde çalışır —
 * SSS akordeonundaki gerekçenin aynısı.
 */

const STORAGE_KEY = 'admin:assistant-open';
const MAX_HISTORY = 6;

// Yanıt içindeki panel adresleri tıklanabilir olur. Yer tutuculu adresler
// (/admin/users/<üye>) bilinçli olarak eşleşmez: üye kimliği olmadan o rota
// açılamaz ve kırık bir bağlantı, düz metinden kötüdür.
const PATH_RE = /\/admin(?:\/[a-z0-9-]+)*/gi;
const INLINE_RE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|\/admin(?:\/[a-z0-9-]+)*)/gi;

const linkablePath = (value) => {
  const path = String(value || '').trim().split(/[\s·,)]/)[0];
  return /^\/admin(\/[a-z0-9-]+)*$/i.test(path) ? path : '';
};

const renderInline = (text, key, onNavigate) => {
  const parts = String(text).split(INLINE_RE).filter((part) => part !== '');
  return parts.map((part, index) => {
    const nodeKey = `${key}-${index}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={nodeKey}>{part.slice(2, -2)}</strong>;
    }
    if (/^`[^`]+`$/.test(part)) {
      return <code key={nodeKey}>{part.slice(1, -1)}</code>;
    }
    PATH_RE.lastIndex = 0;
    if (PATH_RE.test(part) && linkablePath(part)) {
      return (
        <button
          type="button"
          key={nodeKey}
          className="admin-assistant-path"
          onClick={() => onNavigate(linkablePath(part))}
        >
          {part}
        </button>
      );
    }
    return <span key={nodeKey}>{part}</span>;
  });
};

/** Modelin döndürdüğü hafif işaretlemeyi (kalın, madde, başlık) çizer. */
const renderAnswer = (text, onNavigate) => {
  const lines = String(text || '').split('\n');
  const nodes = [];
  let bullets = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    nodes.push(
      <ul key={`ul-${nodes.length}`}>
        {bullets.map((item, index) => (
          <li key={`li-${nodes.length}-${index}`}>
            {renderInline(item, `li-${nodes.length}-${index}`, onNavigate)}
          </li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((raw, index) => {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      return;
    }
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      bullets.push(bullet[1]);
      return;
    }
    flushBullets();
    const heading = line.match(/^#{1,4}\s+(.*)$/);
    if (heading) {
      nodes.push(
        <p key={`h-${index}`} className="admin-assistant-heading">
          {renderInline(heading[1], `h-${index}`, onNavigate)}
        </p>,
      );
      return;
    }
    nodes.push(
      <p key={`p-${index}`}>{renderInline(line, `p-${index}`, onNavigate)}</p>,
    );
  });
  flushBullets();
  return nodes;
};

const errorDetail = (requestError) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return 'Asistana ulaşılamadı. Birazdan tekrar deneyin.';
};

const AdminAssistant = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      setOpen(localStorage.getItem(STORAGE_KEY) === '1');
    } catch (storageError) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!open || suggestions.length) return undefined;
    let active = true;
    client.get('/admin/assistant/meta')
      .then(({ data }) => {
        if (active) setSuggestions(data?.suggestions || []);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [open, suggestions.length]);

  useEffect(() => {
    if (!open) return;
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, busy, open]);

  const toggle = useCallback((next) => {
    setOpen(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
    } catch (storageError) { /* tercih saklanamadı, önemsiz */ }
    if (next) window.setTimeout(() => inputRef.current?.focus(), 120);
  }, []);

  const goTo = useCallback((path) => {
    toggle(false);
    navigate(path);
  }, [navigate, toggle]);

  const send = useCallback(async (text) => {
    const value = String(text || '').trim();
    if (!value || busy) return;
    // Geçmiş sunucuya gönderilirken hata balonları dışarıda bırakılır:
    // modelin kendi hatasını bağlam sanması yanıtı bozar.
    const history = messages
      .filter((item) => !item.error)
      .slice(-MAX_HISTORY)
      .map((item) => ({ role: item.role, content: item.content }));

    setMessages((current) => [...current, { role: 'user', content: value }]);
    setQuestion('');
    setBusy(true);
    try {
      const { data } = await client.post('/admin/assistant/chat', {
        question: value,
        history,
      });
      setMessages((current) => [...current, {
        role: 'assistant',
        content: data?.answer || '',
        sources: data?.sources || [],
        fallback: Boolean(data?.fallback),
      }]);
    } catch (requestError) {
      setMessages((current) => [...current, {
        role: 'assistant',
        content: errorDetail(requestError),
        error: true,
      }]);
    } finally {
      setBusy(false);
      window.setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [busy, messages]);

  const onSubmit = (event) => {
    event.preventDefault();
    send(question);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          className="admin-assistant-fab"
          onClick={() => toggle(true)}
          title="Panel Asistanı — panel hakkında soru sorun"
        >
          <Bot size={20} />
          <span>Panel Asistanı</span>
        </button>
      )}

      {open && (
        <section className="admin-assistant" aria-label="Panel Asistanı">
          <header className="admin-assistant-head">
            <span className="admin-assistant-mark"><Sparkles size={15} /></span>
            <div className="min-w-0">
              <strong>Panel Asistanı</strong>
              <span className="admin-assistant-sub">
                Panel hakkında sorularınızı yanıtlar.
              </span>
            </div>
            {messages.length > 0 && (
              <button
                type="button"
                className="admin-assistant-icon-btn"
                onClick={() => setMessages([])}
                title="Sohbeti temizle"
              >
                <Trash2 size={15} />
              </button>
            )}
            <button
              type="button"
              className="admin-assistant-icon-btn"
              onClick={() => toggle(false)}
              title="Kapat"
            >
              <X size={16} />
            </button>
          </header>

          <div className="admin-assistant-body" ref={listRef}>
            {messages.length === 0 && (
              <div className="admin-assistant-intro">
                <p>
                  Panelin hangi ekranında ne yapıldığını sorun; yolu ve
                  gereken yetkiyi söyleyeyim. Site dışı konularda yardımcı
                  olamam.
                </p>
                {suggestions.map((item) => (
                  <button
                    type="button"
                    key={item}
                    className="admin-assistant-suggestion"
                    onClick={() => send(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}

            {messages.map((item, index) => (
              <div
                key={`${item.role}-${index}`}
                className={[
                  'admin-assistant-msg',
                  item.role === 'user' ? 'is-user' : 'is-bot',
                  item.error ? 'is-error' : '',
                ].join(' ').trim()}
              >
                {item.role === 'user'
                  ? <p>{item.content}</p>
                  : renderAnswer(item.content, goTo)}
                {item.sources?.length > 0 && !item.fallback && (
                  <div className="admin-assistant-sources">
                    {item.sources.slice(0, 3).map((source) => {
                      const path = linkablePath(source.path);
                      if (!path) {
                        return (
                          <span
                            key={source.anchor}
                            className="admin-assistant-source"
                          >
                            {source.title}
                          </span>
                        );
                      }
                      return (
                        <button
                          type="button"
                          key={source.anchor}
                          className="admin-assistant-source is-link"
                          onClick={() => goTo(path)}
                        >
                          {source.title}
                          <ArrowUpRight size={12} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {busy && (
              <div className="admin-assistant-msg is-bot is-busy">
                <Loader2 size={14} className="admin-assistant-spin" />
                <span>Kılavuza bakıyorum…</span>
              </div>
            )}
          </div>

          <form className="admin-assistant-foot" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              className="admin-input"
              value={question}
              maxLength={1000}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Örn: bekleyen çekimi nasıl onaylarım?"
            />
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={busy || !question.trim()}
              title="Gönder"
            >
              <Send size={15} />
            </button>
          </form>
        </section>
      )}
    </>
  );
};

export default AdminAssistant;
